import { ExperimentQualifier, BrowserAdapter } from '@mkt-client/experiment-qualifier';
import * as Sentry from '@sentry/sveltekit';
import { AnalyticsService } from '$lib/services/analytics';
import { app, setGlobalStore } from '$lib/stores/app/store';
import { get } from 'svelte/store';
import { LOCAL_STORAGE_KEYS } from '$lib/utils/constants';
import { ProductExperienceService } from '$lib/services/product-experience';
import { AOFL_PRODUCT_NAMES } from '$lib/utils/constants';

/** @constant {string} Key used to fetch experiments configuration */
const EXPERIMENTS_CONFIG_KEY = 'experiments.json';

/**
 * Fetches the experiments configuration from the server
 * @returns {Promise<object>} The experiments configuration object
 * @property {Array} experiments - Array of experiment configurations
 * @property {object} experiment_arms - Map of experiment arms keyed by experiment ID
 * @property {object} feature_flags - Map of feature flag configurations
 */
const getConfig = async () => {
	const fallbackConfig = {
		experiments: [],
		experiment_arms: {},
		feature_flags: {}
	};

	try {
		const configResp = await fetch(`${window.location.origin}/${EXPERIMENTS_CONFIG_KEY}`);
		const config = await configResp.json();
		return config;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Experiments Config Failed To Fetch'
			}
		});
		return fallbackConfig;
	}
};

/**
 * Builds the request context for experiment evaluation
 * @returns {object} The request context object containing user detection and analytics data
 * @property {object} userDetection - User detection information
 * @property {string} src - Analytics source tag
 * @property {object} params - URL search parameters
 */
const getRequestContext = () => {
	// TODO: clean this up later, consolidate with infra-utils
	const { meta, global } = get(app);

	const paramsObj = new URLSearchParams(window.location.search);
	const params = Object.fromEntries(paramsObj.entries());
	return {
		...meta,
		...global,
		src: AnalyticsService.getSourceTag(),
		params
	};
};

/**
 * Qualifies experiments and feature flags based on configuration and context, storing results in localStorage
 * @returns {Promise<void>}
 * @throws {Error} If experiment qualification fails
 */
export const qualifyExperiments = async () => {
	const config = await getConfig();

	try {
		const requestContext = getRequestContext();
		const browserAdapter = new BrowserAdapter();
		const experimentQualifier = new ExperimentQualifier(config, browserAdapter);

		const result = experimentQualifier.selectExperimentArms();
		const original_result = result;
		delete result.disallowed;
		delete result.reason;
		delete result.first_assignment;

		const experiments = ExperimentQualifier.evaluateConditionFromHeader(result, requestContext);

		setTimeout(async () => {
			await AnalyticsService.trackClientDebug({
				message: 'Success: experiments_qualification',
				experiments: experiments,
				requestContext: requestContext,
				result: original_result
			});
		}, 2000);

		// Store only the experiment assignments
		const experimentAssignments = {};
		for (const [expId, exp] of Object.entries(experiments)) {
			if (exp && typeof exp === 'object' && 'id' in exp) {
				experimentAssignments[expId] = {
					id: exp.id,
					condition_expression: exp.condition_expression
				};
			}
		}

		localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, JSON.stringify(experimentAssignments));

		// Process and store feature flags
		if (config.feature_flags) {
			const processedFeatureFlags = processFeatureFlags(config.feature_flags);
			await setGlobalStore({ featureFlags: processedFeatureFlags });
		}
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Get Experiments'
			}
		});
	}
};

/**
 * Checks if a specific experiment arm is currently active
 * if platform is set return false
 * @param {string} armId - The ID of the experiment arm to check
 * @param {boolean} trackAnalyticsService - Flag to track the experiment assignment in analytics
 * @returns {boolean} - True if the arm is active, false otherwise
 * @throws {Error} If localStorage access or JSON parsing fails (caught internally)
 * @example
 * // Check if a specific arm is active
 * if (isExperimentArmActive('8892')) {
 *   // Show variant content
 * } else {
 *   // Show control content
 * }
 */
export const isExperimentArmActive = (armId, trackAnalyticsService = true) => {
	try {
		const experimentsJson = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
		if (!experimentsJson) return false;

		const experiments = JSON.parse(experimentsJson);
		// Check all experiments to see if any have the specified arm ID
		const isActive = Object.values(experiments).some((experiment) => {
			return experiment?.id === armId;
		});
		if (isActive && trackAnalyticsService) {
			// TODO: fire rotation assignment event from logger when done
			AnalyticsService.trackExperimentAssignment({
				rotationId: armId
			});
		}

		return isActive;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Check Experiment Arm',
				armId
			}
		});
		return false;
	}
};

/**
 * Change the experiments configuration based on the experiment arm ID
 * @param {string} fromArmId - The ID of the experiment arm switch from
 * @param {string} toArmId - The ID of the experiment arm to switch to
 * @param {{force: boolean}} options - Options for the change
 * @returns {Promise<boolean>} - True if the arm is active, false otherwise
 * @throws {Error} If localStorage access or JSON parsing fails (caught internally)
 * @example
 * // Change experiment arm from one to another
 * await changeExperiment('10019', '10020', { force: true });
 */
export const changeExperiment = async (fromArmId, toArmId, { force = true }) => {
	try {
		const experimentsJson = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
		if (!experimentsJson) return false;

		let experiments = JSON.parse(experimentsJson);
		for (const key in experiments) {
			let experiment = experiments[key];
			if (experiment.id === fromArmId) {
				experiments[key]['id'] = toArmId;
				localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, JSON.stringify(experiments));
				return true;
			}
		}
		return false;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Check Experiment Arm',
				toArmId
			}
		});

		if (force) forceArmFallback(toArmId);

		return false;
	}
};

/**
 * This option will only be run in the case of a failed experiment arm change
 * Forces the experiment arm to a specified ID by setting a fallback experiment configuration in localStorage.
 * This method is used to ensure an experiment arm is active even if the original configuration fails to load.
 * @param {string} toArmId - The ID of the experiment arm to force.
 */
export const forceArmFallback = async (toArmId) => {
	try {
		// LOG ERROR
		const FALLBACK_EXPERIMENT_ID = '999999';
		let experiments = {
			[FALLBACK_EXPERIMENT_ID]: {
				id: toArmId,
				condition_expression: 'true'
			}
		};
		localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, JSON.stringify(experiments));
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Force Experiment Arm',
				toArmId
			}
		});
	}
};

/**
 * Checks if a value is truthy, handling boolean values and string "true"/"false"
 * @param {*} value - The value to check
 * @returns {boolean} - True if the value is truthy, false otherwise
 */
const isTruthy = (value) => {
	if (typeof value === 'boolean') {
		return value;
	}
	if (typeof value === 'string') {
		const lowerValue = value.toLowerCase();
		return lowerValue === 'true';
	}
	return false;
};

/**
 * Gets feature flag overrides from query parameters
 * @returns {object} - Object containing feature flag overrides from URL parameters
 */
const getFeatureFlagOverridesFromUrl = () => {
	try {
		const urlParams = new URLSearchParams(window.location.search);
		const overrides = {};

		for (const [key, value] of urlParams.entries()) {
			if (key.startsWith('ff_')) {
				const flagKey = key.substring(3); // Remove 'ff_' prefix
				overrides[flagKey] = isTruthy(value);
			}
		}

		return overrides;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Get Feature Flag Overrides From URL'
			}
		});
		return {};
	}
};

/**
 * Processes feature flags with URL overrides and forceFlagForAll logic
 * @param {object} configFeatureFlags - Feature flags from the config
 * @returns {object} - Processed feature flags ready for app store
 */
const processFeatureFlags = (configFeatureFlags) => {
	const currentApp = get(app);
	const existingFeatureFlags = currentApp.global.featureFlags || {};
	const urlOverrides = getFeatureFlagOverridesFromUrl();
	const newFeatureFlags = {};

	for (const [flagKey, flagConfig] of Object.entries(configFeatureFlags)) {
		if (flagConfig && typeof flagConfig === 'object' && 'status' in flagConfig) {
			// Check if this flag already exists in the app store
			const existingFlag = existingFeatureFlags[flagKey];
			const forceFlagForAll = flagConfig.forceFlagForAll === true;
			const hasUrlOverride = Object.prototype.hasOwnProperty.call(urlOverrides, flagKey);

			// URL overrides take highest precedence
			if (hasUrlOverride) {
				newFeatureFlags[flagKey] = {
					status: urlOverrides[flagKey] ? 'true' : 'false',
					meta: flagConfig.meta || []
				};
			}
			// Only override existing flags if forceFlagForAll is true
			else if (!existingFlag || forceFlagForAll) {
				newFeatureFlags[flagKey] = {
					status: flagConfig.status,
					meta: flagConfig.meta || []
				};
			} else {
				// Keep the existing flag
				newFeatureFlags[flagKey] = existingFlag;
			}
		}
	}

	return newFeatureFlags;
};



/**
 * Checks if a specific feature flag is currently enabled
 * @param {string} flagKey - The key of the feature flag to check
 * @returns {boolean} - True if the feature flag is enabled, false otherwise
 * @example
 * // Check if a specific feature flag is enabled
 * if (isFeatureFlagEnabled('2_0_test')) {
 *   // Show new feature
 * } else {
 *   // Show legacy feature
 * }
 */
export const isFeatureFlagEnabled = (flagKey) => {
	try {
		// Get feature flags from app store
		const currentApp = get(app);
		const featureFlags = currentApp.global.featureFlags || {};
		const flag = featureFlags[flagKey];

		if (!flag) return false;

		// TODO: temporary for the 2.0 web experiment.
		if (flagKey === '2_0_test' && isTruthy(flag.status)) {
			ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
		}

		// Check if the status is truthy using our robust checker
		return isTruthy(flag.status);
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: 'Error: Failed To Check Feature Flag',
				flagKey
			}
		});
		return false;
	}
};
